import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight } from "lucide-react";
import { inr } from "@/lib/api";

const STATUS_STYLES = {
  published: "bg-emerald-100 text-emerald-800 border-emerald-200",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  joined: "bg-emerald-100 text-emerald-800 border-emerald-200",
  paid: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  accepted: "bg-emerald-100 text-emerald-800 border-emerald-200",
  pending_review: "bg-amber-100 text-amber-800 border-amber-200",
  resubmitted: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  submitted: "bg-amber-100 text-amber-800 border-amber-200",
  eligible: "bg-blue-100 text-blue-800 border-blue-200",
  interview: "bg-blue-100 text-blue-800 border-blue-200",
  shortlisted: "bg-blue-100 text-blue-800 border-blue-200",
  selected: "bg-blue-100 text-blue-800 border-blue-200",
  offer: "bg-blue-100 text-blue-800 border-blue-200",
  enrolled: "bg-blue-100 text-blue-800 border-blue-200",
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  screening: "bg-slate-100 text-slate-700 border-slate-200",
  correction_required: "bg-orange-100 text-orange-800 border-orange-200",
  on_hold: "bg-orange-100 text-orange-800 border-orange-200",
  hold: "bg-orange-100 text-orange-800 border-orange-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
  declined: "bg-red-100 text-red-800 border-red-200",
  failed: "bg-red-100 text-red-800 border-red-200",
  suspended: "bg-red-100 text-red-800 border-red-200",
  closed: "bg-slate-200 text-slate-600 border-slate-300",
  due: "bg-orange-100 text-orange-800 border-orange-200",
  applied: "bg-sky-100 text-sky-800 border-sky-200",
  assessment: "bg-violet-100 text-violet-800 border-violet-200",
  final_interview: "bg-violet-100 text-violet-800 border-violet-200",
  bgv: "bg-cyan-100 text-cyan-800 border-cyan-200",
  signed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  fnf_processed: "bg-blue-100 text-blue-800 border-blue-200",
};

const STATUS_LABELS = { fnf_processed: "F&F Processed" };

export function StatusBadge({ status }) {
  const style = STATUS_STYLES[status] || "bg-slate-100 text-slate-700 border-slate-200";
  const label = STATUS_LABELS[status] || (status || "").replace(/_/g, " ");
  return (
    <Badge variant="outline" className={`${style} font-medium capitalize`} data-testid={`status-${status}`}>
      {label}
    </Badge>
  );
}

export function StatCard({ label, value, icon: Icon, accent = false, testId }) {
  return (
    <div
      className="bg-white border border-border/60 rounded-xl p-5 card-hover"
      data-testid={testId || `stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground font-medium">{label}</p>
          <p className={`text-3xl font-heading font-bold mt-1.5 ${accent ? "gold-text" : "text-[#0B1B3D]"}`}>
            {value}
          </p>
        </div>
        {Icon && (
          <div className={`p-2.5 rounded-lg ${accent ? "bg-[#F5A623]/15" : "bg-[#2563EB]/10"}`}>
            <Icon className={`w-5 h-5 ${accent ? "text-[#F5A623]" : "text-[#2563EB]"}`} />
          </div>
        )}
      </div>
    </div>
  );
}

export function EmptyState({ title, description, cta, onCta, testId }) {
  return (
    <div
      className="border border-dashed border-border rounded-xl py-16 px-6 text-center bg-white/60"
      data-testid={testId || "empty-state"}
    >
      <p className="font-heading font-semibold text-lg text-[#0B1B3D]">{title}</p>
      {description && <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">{description}</p>}
      {cta && (
        <Button onClick={onCta} className="mt-6 rounded-full" data-testid="empty-state-cta">
          {cta} <ArrowRight className="w-4 h-4 ml-1" />
        </Button>
      )}
    </div>
  );
}

export function PageHeader({ title, description, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight text-[#0B1B3D]">{title}</h1>
        {description && <p className="text-sm text-muted-foreground mt-1.5">{description}</p>}
      </div>
      {children && <div className="flex gap-3">{children}</div>}
    </div>
  );
}

export function LoadingGrid({ count = 4 }) {
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, i) => (
        <Skeleton key={i} className="h-28 rounded-xl" />
      ))}
    </div>
  );
}

export { inr };
